import {
  ShiftTypeDto,
  TsSchicht,
  useDeleteShift,
  useDepartmentDutyRosterAddShift,
  useDepartmentDutyRosterEditShift,
  useGetShiftSuspense,
  useGetShiftTypeSuspense,
} from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CancelButton, DeleteButton, SubmitButton } from "@components/Buttons"
import {
  Group,
  ModalProps,
  Stack,
  Textarea,
  Text,
  LoadingOverlay,
  Select,
} from "@mantine/core"
import { TimeInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { match } from "ts-pattern"
import { useRequestablePresenceShiftTypes } from "@/blocks/shift-types/useRequestablePresenceShiftTypes"

interface SharedProps extends ModalProps {
  onSuccess: () => void
}

interface CreateShiftModalProps extends SharedProps {
  type: "create"
  shiftTypeId: number
  contractId: number
  date: string
}

interface CreateShiftWithTypeSelectModalProps extends SharedProps {
  type: "create-with-shift-type-select"
  shiftTypeId?: number
  departmentId: number
  contractId: number
  date: string
}

interface EditShiftModalProps extends SharedProps {
  type: "edit"
  dutyRosterId: number
  shiftId: TsSchicht
  contractId: number
  date: string
}

export function CreateOrEditShiftForm(
  props:
    | CreateShiftModalProps
    | CreateShiftWithTypeSelectModalProps
    | EditShiftModalProps
) {
  return props.type === "create" ? (
    <CreateShiftModal {...props} />
  ) : props.type === "create-with-shift-type-select" ? (
    <CreateShiftWithTypeSelectModal {...props} />
  ) : (
    <EditShiftModal {...props} />
  )
}

function CreateShiftModal(props: CreateShiftModalProps) {
  const createShift = useDepartmentDutyRosterAddShift({
    mutation: { onSuccess: props.onSuccess, onError: showErrorNotification },
  })
  const { data: shiftType } = useGetShiftTypeSuspense({
    shiftTypeId: props.shiftTypeId,
  })
  const handleSubmit = (values: FormValues) => {
    createShift.mutate({
      data: {
        shiftTypeId: props.shiftTypeId,
        contractId: props.contractId,
        date: props.date,
        startTime: values.startTime,
        endTime: values.endTime,
        shiftNote: values.note,
      },
    })
  }

  return (
    <Form
      shiftType={shiftType}
      onCancel={props.onClose}
      onSubmit={handleSubmit}
      isPending={createShift.isPending}
    />
  )
}

function CreateShiftWithTypeSelectModal(
  props: CreateShiftWithTypeSelectModalProps
) {
  const createShift = useDepartmentDutyRosterAddShift({
    mutation: { onSuccess: props.onSuccess, onError: showErrorNotification },
  })
  const { data: shiftTypes } = useRequestablePresenceShiftTypes({
    departmentId: props.departmentId,
  })
  const handleSubmit = (values: FormValues) => {
    if (values.shiftTypeId === undefined) {
      return
    }

    createShift.mutate({
      data: {
        shiftTypeId: values.shiftTypeId,
        contractId: props.contractId,
        date: props.date,
        startTime: values.startTime,
        endTime: values.endTime,
        shiftNote: values.note,
      },
    })
  }

  return (
    <Form
      shiftTypes={shiftTypes}
      onCancel={props.onClose}
      onSubmit={handleSubmit}
      isPending={createShift.isPending}
      formValues={{ shiftTypeId: props.shiftTypeId }}
    />
  )
}

function EditShiftModal(props: EditShiftModalProps) {
  const editShift = useDepartmentDutyRosterEditShift({
    mutation: { onSuccess: props.onSuccess, onError: showErrorNotification },
  })
  const { data } = useGetShiftSuspense({
    dutyRosterId: props.dutyRosterId,
    shiftId: props.shiftId,
  })
  const { data: shiftType } = useGetShiftTypeSuspense({
    shiftTypeId: data.shiftTypeId,
  })
  const shiftId = getShiftId(props.shiftId)
  const handleSubmit = (values: FormValues) => {
    editShift.mutate({
      data: {
        shiftId: shiftId,
        startTime: values.startTime,
        endTime: values.endTime,
        shiftNote: values.note,
        shiftTypeId: data.shiftTypeId,
        contractId: props.contractId,
        date: props.date,
      },
    })
  }
  const deleteShift = useDeleteShift({
    mutation: { onSuccess: props.onSuccess },
  })
  const handleDeleteShift = async () => {
    await deleteShift.mutateAsync({
      data: { dutyRosterId: props.dutyRosterId, shiftId: shiftId },
    })
  }

  return (
    <Form
      shiftType={shiftType}
      onCancel={props.onClose}
      onSubmit={handleSubmit}
      additionalButtons={<DeleteButton onClick={handleDeleteShift} />}
      formValues={{
        startTime: data.startTime,
        endTime: data.endTime,
        note: data.shiftNote,
      }}
      isPending={editShift.isPending}
    />
  )
}

interface FormValues {
  shiftTypeId?: number
  startTime?: string | null | undefined
  endTime?: string | null | undefined
  note?: string | null | undefined
}

interface FormProps {
  shiftType?: ShiftTypeDto
  shiftTypes?: ShiftTypeDto[]
  onCancel: () => void
  onSubmit: (values: FormValues) => void
  additionalButtons?: React.ReactNode
  formValues?: FormValues
  isPending?: boolean
}

function Form({
  shiftType,
  shiftTypes,
  onCancel,
  onSubmit,
  additionalButtons,
  formValues,
  isPending,
}: FormProps) {
  const { t } = useTranslation("app_server")
  const form = useForm<FormValues>({
    initialValues: {
      ...formValues,
    },
    validate: {
      shiftTypeId: (value) =>
        shiftTypes && value === undefined ? t("RequiredField") : null,
    },
  })
  const selectedShiftType =
    shiftTypes?.find(
      (value) => value.shiftTypeId === form.values.shiftTypeId
    ) ?? shiftType
  const defaultShift1 = getDefaultTimes({
    startTime: selectedShiftType?.startTimeDefault1,
    endTime: selectedShiftType?.endTimeDefault1,
  })
  const defaultShift2 = getDefaultTimes({
    startTime: selectedShiftType?.startTimeDefault2,
    endTime: selectedShiftType?.endTimeDefault2,
  })
  const defaultShift3 = getDefaultTimes({
    startTime: selectedShiftType?.startTimeDefault3,
    endTime: selectedShiftType?.endTimeDefault3,
  })

  const defaultShifts = [defaultShift1, defaultShift2, defaultShift3].filter(
    Boolean
  ) as { startTime: string; endTime: string }[]
  const hasDefaultTimes = defaultShift1 || defaultShift2 || defaultShift3
  const withTimeInput =
    Boolean(selectedShiftType) &&
    !hasDefaultTimes &&
    !selectedShiftType?.isAbsent
  React.useEffect(() => {
    form.setValues({
      ...formValues,
    })
  }, [formValues])

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <LoadingOverlay visible={isPending} />
      <Stack>
        {shiftTypes ? (
          <Select
            required
            label={t("ShiftType")}
            data={shiftTypes.flatMap((value) =>
              value.shiftTypeId === undefined
                ? []
                : [
                    {
                      label: value.name ?? "",
                      value: value.shiftTypeId.toString(),
                    },
                  ]
            )}
            value={form.values.shiftTypeId?.toString() ?? null}
            onChange={(value) =>
              form.setFieldValue(
                "shiftTypeId",
                value === null ? undefined : Number(value)
              )
            }
            error={form.errors.shiftTypeId}
          />
        ) : (
          <Text>
            {defaultShifts.length > 0
              ? selectedShiftType?.name +
                " (" +
                defaultShifts
                  .map((s) => s.startTime + " - " + s.endTime)
                  .join(", ") +
                ")"
              : selectedShiftType?.name}
          </Text>
        )}
        {withTimeInput && (
          <Group>
            <TimeInput {...form.getInputProps("startTime")} label={t("From")} />
            <TimeInput {...form.getInputProps("endTime")} label={t("Until")} />
          </Group>
        )}
        <Textarea
          {...form.getInputProps("note")}
          key={form.key("note")}
          label={t("Note")}
        />
        <Group justify="end">
          <CancelButton onClick={onCancel} />
          {additionalButtons}
          <SubmitButton>{t("EnterShift")}</SubmitButton>
        </Group>
      </Stack>
    </form>
  )
}

function getShiftId(shiftId: TsSchicht): number {
  return match(shiftId)
    .with("Schicht1", () => 1)
    .with("Schicht2", () => 2)
    .with("Schicht3", () => 3)
    .exhaustive()
}

function getDefaultTimes({
  startTime,
  endTime,
}: {
  startTime: string | null | undefined
  endTime: string | null | undefined
}): { startTime: string; endTime: string } | null {
  if (startTime && endTime && startTime !== endTime) {
    return {
      startTime: startTime,
      endTime: endTime,
    }
  }
  return null
}
